/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@mui/material';

import { ClusterStates } from 'shared';

import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { NoItemsText } from '../../../../components/NoItemsText/NoItemsText';
import { PageOverlay } from '../../../../components/PageOverlay/PageOverlay';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { IMPORT_CLUSTER_DATA_SETS_OVERLAY_CSS_ANCHOR } from '../../../../constants/settings';
import { Namespaces } from '../../../../i18n';
import { DatasetImportApiService } from '../../../../services/DatasetImportApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { DatasetCategory } from '../../../../types/dataset/DatasetCategory';
import { DatasetImport } from '../../../../types/dataset/DatasetImport';
import { reactToImportDatasets } from '../../../../utils/reactions';
import { useObjectState } from '../../../../utils/useObjectState';
import { DatasetImportsTable } from './components/DatasetImportsTable/DatasetImportsTable';
import { ImportDataSetsCluster } from './components/ImportDataSetsCluster/ImportDataSetsCluster';
import { useDatasetsTabStyles } from './DatasetsTab.styles';
import {
  ApiErrorDataType,
  CancelDatasetDataType,
  DeleteDatasetDataType,
  ReimportDatasetDataType,
  SetDatasetNotReadonlyDataType,
} from './DatasetsTab.types';

type DatasetsTabProps = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  isClusterUnavailable: boolean;
  datasets: DatasetImport[] | undefined;
  datasetsCategories: DatasetCategory[];
  clusterStatus: ClusterStates | undefined;
};

type ModalState = {
  importModal: { isOpen: boolean };
};

export const DatasetsTab = memo<DatasetsTabProps>(props => {
  const { identificationData, isClusterUnavailable, datasets, datasetsCategories, clusterStatus } = props;
  const classes = useDatasetsTabStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(undefined);

  const [modalState, setModalState] = useObjectState<ModalState>({
    importModal: { isOpen: false },
  });

  const { importModal } = modalState;

  const handleIsProcessing = (isProcessingState: boolean) => setIsProcessing(isProcessingState);

  const [apiError, setApiError] = useState<ApiErrorDataType>({
    message: null,
    isModalOpened: false,
  });

  const [cancelDatasetData, setCancelDatasetData] = useState<CancelDatasetDataType>({
    wantToCancel: null,
    isModalOpened: false,
  });

  const [reimportDatasetData, setReimportDatasetData] = useState<ReimportDatasetDataType>({
    wantToReimport: null,
    isModalOpened: false,
  });

  const [deleteDatasetData, setDeleteDatasetData] = useState<DeleteDatasetDataType>({
    datasetImport: null,
    isModalOpened: false,
  });

  const [setNotReadonlyDatasetData, setSetNotReadonlyDatasetData] = useState<SetDatasetNotReadonlyDataType>({
    datasetImport: null,
    isModalOpened: false,
  });

  const handleApiError = (props: ApiErrorDataType) => {
    const { message, isModalOpened } = props;
    setApiError({ message, isModalOpened });
  };

  const handleCancelDatasetData = (props: CancelDatasetDataType) => {
    const { wantToCancel, isModalOpened } = props;
    setCancelDatasetData({ wantToCancel, isModalOpened });
  };

  const handleReimportDatasetData = (props: ReimportDatasetDataType) => {
    const { wantToReimport, isModalOpened } = props;
    setReimportDatasetData({ wantToReimport, isModalOpened });
  };

  const handleDeleteDatasetData = (params: DeleteDatasetDataType) => {
    setDeleteDatasetData({ ...params });
  };

  const handleSetNotReadonlyDatasetData = (params: SetDatasetNotReadonlyDataType) => {
    setSetNotReadonlyDatasetData({ ...params });
  };

  const onConfirmCancel = async () => {
    const { wantToCancel } = cancelDatasetData;
    if (wantToCancel) {
      handleIsProcessing(true);
      handleCancelDatasetData({
        wantToCancel: null,
        isModalOpened: false,
      });
      try {
        await DatasetImportApiService.deleteClusterDatasetFromQueue({
          ...identificationData,
          datasetImportId: wantToCancel.id,
        });
      } catch (e) {
        const error = e as ApiClientError;
        const errorMessage = error.response?.data.message;
        if (errorMessage) {
          handleApiError({
            message: errorMessage,
            isModalOpened: true,
          });
        } else {
          handleApiError({
            message: null,
            isModalOpened: true,
          });
        }
        handleCancelDatasetData({
          wantToCancel: null,
          isModalOpened: false,
        });
      } finally {
        await reactToImportDatasets({
          ...identificationData,
        });
        handleIsProcessing(false);
      }
    }
  };

  const onConfirmReimport = async () => {
    const { wantToReimport } = reimportDatasetData;
    if (wantToReimport) {
      handleIsProcessing(true);
      handleReimportDatasetData({
        wantToReimport: null,
        isModalOpened: false,
      });
      try {
        await DatasetImportApiService.importClusterDataset({
          ...identificationData,
          data: {
            datasets: [
              {
                datasetId: wantToReimport.dataset.id,
                readonly: true,
                database: wantToReimport.database,
              },
            ],
          },
        });
      } catch (e) {
        const error = e as ApiClientError;
        const errorMessage = error.response?.data.message;
        if (errorMessage) {
          handleApiError({
            message: errorMessage,
            isModalOpened: true,
          });
        } else {
          handleApiError({
            message: null,
            isModalOpened: true,
          });
        }
        handleReimportDatasetData({
          wantToReimport: null,
          isModalOpened: false,
        });
      } finally {
        await reactToImportDatasets({
          ...identificationData,
        });
        handleIsProcessing(false);
      }
    }
  };

  const onConfirmSetNotReadonly = async (datasetImport: DatasetImport) => {
    if (!datasetImport) {
      return;
    }
    handleIsProcessing(true);
    try {
      await DatasetImportApiService.setClusterDatasetModifiable({
        ...identificationData,
        datasetImportId: datasetImport.id,
      });
    } catch (e) {
      const error = e as ApiClientError;
      const errorMessage = error.response?.data.message;
      handleApiError({
        message: errorMessage ?? null,
        isModalOpened: true,
      });
    } finally {
      await reactToImportDatasets({
        ...identificationData,
      });
      handleIsProcessing(false);
    }
  };

  const onConfirmDelete = async (datasetImport: DatasetImport) => {
    if (!datasetImport) {
      return;
    }
    handleIsProcessing(true);
    try {
      await DatasetImportApiService.deleteClusterDataset({
        ...identificationData,
        datasetImportId: datasetImport.id,
      });
    } catch (e) {
      const error = e as ApiClientError;
      const errorMessage = error.response?.data.message;
      handleApiError({
        message: errorMessage ?? null,
        isModalOpened: true,
      });
    } finally {
      await reactToImportDatasets({
        ...identificationData,
      });
      handleIsProcessing(false);
    }
  };

  const toggleImportModal = useCallback(() => {
    setModalState({
      importModal: { isOpen: !importModal.isOpen },
    });
  }, [importModal.isOpen, setModalState]);

  const isImportDisabled = useMemo(() => clusterStatus !== ClusterStates.Running, [clusterStatus]);

  return (
    <>
      <HUSpin spinning={datasets === undefined || isProcessing || isClusterUnavailable} delay={50}>
        {!datasets || !datasets.length ? (
          <NoItemsText className={classes.noItemsLabel} title={t('details.datasets.no_datasets')} />
        ) : datasets.length ? (
          <DatasetImportsTable
            datasetImports={datasets}
            handleCancelDatasetData={handleCancelDatasetData}
            handleReimportDatasetData={handleReimportDatasetData}
            handleDeleteDatasetData={handleDeleteDatasetData}
            handleSetNotReadonlyDatasetData={handleSetNotReadonlyDatasetData}
            containerRef={tableRef}
            clusterAccessor={identificationData}
          />
        ) : null}
        {!!datasetsCategories.length && (
          <Button disabled={isImportDisabled} onClick={toggleImportModal} className={classes.importButton}>
            +&nbsp;{t('details.datasets.import')}
          </Button>
        )}
      </HUSpin>
      <PageOverlay
        open={importModal.isOpen}
        showCloseBtn
        onCloseBtnClick={toggleImportModal}
        className={IMPORT_CLUSTER_DATA_SETS_OVERLAY_CSS_ANCHOR}
        stickyButtons
        name="import-datasets-cluster"
      >
        {importModal.isOpen ? (
          <ImportDataSetsCluster
            onSuccess={toggleImportModal}
            onCancel={toggleImportModal}
            identificationData={identificationData}
          />
        ) : null}
      </PageOverlay>
      <SimpleConfirmModal
        open={cancelDatasetData.isModalOpened}
        message={t('details.datasets.dataset.cancel_confirm_message', {
          dataset_name: cancelDatasetData.wantToCancel?.dataset?.name,
        })}
        onCancel={() => handleCancelDatasetData({ wantToCancel: null, isModalOpened: false })}
        onConfirm={onConfirmCancel}
      />
      <SimpleConfirmModal
        open={reimportDatasetData.isModalOpened}
        message={t('details.datasets.dataset.reimport_confirm_message', {
          dataset_name: reimportDatasetData.wantToReimport?.dataset?.name,
        })}
        onCancel={() =>
          handleReimportDatasetData({
            wantToReimport: null,
            isModalOpened: false,
          })
        }
        onConfirm={onConfirmReimport}
      />
      <SimpleConfirmModal
        open={setNotReadonlyDatasetData.isModalOpened}
        message={t('details.datasets.dataset.set_not_readonly_confirm_message', {
          dataset_name: setNotReadonlyDatasetData.datasetImport?.dataset?.name,
        })}
        onCancel={() =>
          handleSetNotReadonlyDatasetData({
            datasetImport: null,
            isModalOpened: false,
          })
        }
        onConfirm={() => {
          handleSetNotReadonlyDatasetData({
            datasetImport: null,
            isModalOpened: false,
          });
          void onConfirmSetNotReadonly(setNotReadonlyDatasetData.datasetImport!);
        }}
      />
      <SimpleConfirmModal
        open={deleteDatasetData.isModalOpened}
        message={t('details.datasets.dataset.delete_confirm_message', {
          dataset_name: deleteDatasetData.datasetImport?.dataset?.name,
        })}
        onCancel={() =>
          handleDeleteDatasetData({
            datasetImport: null,
            isModalOpened: false,
          })
        }
        onConfirm={() => {
          handleDeleteDatasetData({
            datasetImport: null,
            isModalOpened: false,
          });
          void onConfirmDelete(deleteDatasetData.datasetImport!);
        }}
      />
      <SimpleNotifyModal
        open={apiError.isModalOpened}
        message={t([
          `${Namespaces.Common}:dataset_import_errors.${apiError.message}`,
          `${Namespaces.Common}:errors.unexpected_error`,
        ])}
        onOk={() => handleApiError({ message: null, isModalOpened: false })}
      />
    </>
  );
});
